import { AnnotationType, AssignmentStatus, PieceAnnotation, TrackingNumberStatus } from 'src/types/apiContract';
import { ReviewItemsFilterType } from 'src/types/shared';

import { CaptureId, CapturePiece } from '../../types';

type AccType = {
  items: CapturePiece[];
  isActiveInList: boolean;
  requireValidation: number;
  unassigned: number;
  processing: number;
  missingTracking: number;
  duplicate: number;
};

const isTrackingInclude = (searchString: string, annotations?: Array<PieceAnnotation> | null) => {
  return !!annotations?.some((annotation) => {
    if (annotation.annotationType === AnnotationType.TrackingNumber) {
      return !!annotation.textValue?.toLowerCase().includes(searchString);
    }
    return false;
  });
};

// SINGLE REDUCE FOR SEARCH, FILTER, COUNTING
export const prepareItems = (
  pieces: CapturePiece[],
  filter: ReviewItemsFilterType | null,
  activeId: CaptureId,
  search?: string,
) =>
  pieces.reduce<AccType>(
    (acc, item) => {
      const piece = item.piece;
      const recipient = piece?.recipient;

      // Search check — affects only what goes into `acc.items`, NOT counters.
      // `true` means "no search" OR "recipient matches search".
      const lowerSearch = search?.toLowerCase();
      const isPassedBySearch =
        !lowerSearch ||
        !!(
          (recipient &&
            (recipient.displayName?.toLowerCase().includes(lowerSearch) ||
              recipient.user?.userName?.includes(lowerSearch) ||
              recipient.user?.autoAssignId?.includes(lowerSearch))) ||
          isTrackingInclude(lowerSearch, item.pieceAnnotations)
        );

      // No filter selected — push item into list if it passes search.
      if (!filter && isPassedBySearch) acc.items.push(item);

      const {
        pieceAssignmentStatus,
        trackingNumberStatus,
        inProgressAutoAssign,
        inProgressTrackingNumberDetection,
        inProgressLockerAnnouncement,
      } = item.piece?.pieceSummary || {};
      // Processing item
      if (!piece || inProgressAutoAssign || inProgressTrackingNumberDetection || inProgressLockerAnnouncement) {
        acc.processing += 1;
        return acc;
      }

      // I HATE TYPESCRIPT
      // ALL FILTER OPTION COVER
      if (
        filter === ReviewItemsFilterType.ALL &&
        isPassedBySearch &&
        ([AssignmentStatus.RequireValidation, AssignmentStatus.Unassigned].includes(
          pieceAssignmentStatus as AssignmentStatus,
        ) ||
          [TrackingNumberStatus.Duplicate, TrackingNumberStatus.Missing].includes(
            trackingNumberStatus as TrackingNumberStatus,
          ))
      ) {
        acc.items.push(item);
      }

      // INDIVIDUAL OPTION FILTER COVER
      if (pieceAssignmentStatus === AssignmentStatus.RequireValidation) {
        acc.requireValidation += 1;
        if (filter === ReviewItemsFilterType.REQUIRE_VALIDATION && isPassedBySearch) {
          acc.items.push(item);
          if (item.id === activeId) acc.isActiveInList = true;
        }
      }
      if (pieceAssignmentStatus === AssignmentStatus.Unassigned) {
        acc.unassigned += 1;
        if (filter === ReviewItemsFilterType.UNASSIGNED && isPassedBySearch) {
          acc.items.push(item);
          if (item.id === activeId) acc.isActiveInList = true;
        }
      }
      if (trackingNumberStatus === TrackingNumberStatus.Duplicate) {
        acc.duplicate += 1;
        if (filter === ReviewItemsFilterType.DUPLICATE && isPassedBySearch) {
          acc.items.push(item);
          if (item.id === activeId) acc.isActiveInList = true;
        }
      }
      if (trackingNumberStatus === TrackingNumberStatus.Missing) {
        acc.missingTracking += 1;
        if (filter === ReviewItemsFilterType.MISSING_TRACKING_NUMBER && isPassedBySearch) {
          acc.items.push(item);
          if (item.id === activeId) acc.isActiveInList = true;
        }
      }
      return acc;
    },
    {
      items: [],
      isActiveInList: !filter || filter === ReviewItemsFilterType.ALL,
      requireValidation: 0,
      unassigned: 0,
      missingTracking: 0,
      duplicate: 0,
      processing: 0,
    },
  );
